import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Collapse,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";

const PrintControls = ({
  visible,
  labelFormat,
  onFormatChange,
  selectedPrinter,
  onPrint,
  onConfigurePrinter,
  labelLoading,
  labelError,
  onRetry,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  if (!visible) return null;

  return (
    <Collapse in={visible} timeout={500}>
      <Box sx={{ mt: { xs: 2, md: 3 } }}>
        {selectedPrinter && (
          <Alert
            severity="success"
            sx={{
              mb: { xs: 2, md: 3 },
              maxWidth: { xs: "100%", md: 600 },
              mx: "auto",
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}
          >
            Impressora configurada: {selectedPrinter.name}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: { xs: 2, md: 2 },
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            p: { xs: 2, md: 3 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(255, 152, 0, 0.1)",
            border: "1px solid rgba(255, 152, 0, 0.2)",
          }}
        >
          <FormControl
            size={isXs ? "medium" : "small"}
            sx={{
              minWidth: { xs: "100%", md: 120 },
              "& .MuiInputBase-root": {
                minHeight: { xs: 48, md: "auto" },
              },
              "& .MuiInputLabel-root": {
                fontSize: { xs: "1rem", md: "0.875rem" },
              },
            }}
          >
            <InputLabel>Formato</InputLabel>
            <Select
              value={labelFormat}
              label="Formato"
              onChange={(e) => onFormatChange(e.target.value)}
            >
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="zpl">ZPL</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={onPrint}
            disabled={labelLoading}
            startIcon={
              labelLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PrintIcon />
              )
            }
            sx={{
              minWidth: { xs: "100%", md: 160 },
              minHeight: { xs: 48, md: "auto" },
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "1rem", md: "0.875rem" },
              borderRadius: 1.5,
              backgroundColor: "#ff9800",
              transition: "all 0.2s ease-in-out",
              py: { xs: 1.5, md: 1 },
              "&:hover": {
                backgroundColor: "#f57c00",
                transform: { xs: "none", md: "translateY(-2px)" },
                boxShadow: "0 6px 16px rgba(255, 152, 0, 0.3)",
              },
            }}
          >
            {labelLoading ? "Imprimindo..." : "Imprimir Etiqueta"}
          </Button>

          {selectedPrinter && (
            <Button
              variant="outlined"
              onClick={onConfigurePrinter}
              startIcon={<SettingsIcon />}
              sx={{
                minWidth: { xs: "100%", md: 160 },
                minHeight: { xs: 48, md: "auto" },
                textTransform: "none",
                fontWeight: 500,
                fontSize: { xs: "1rem", md: "0.875rem" },
                borderRadius: 1.5,
                borderColor: "rgba(255, 152, 0, 0.6)",
                color: "#ff9800",
                transition: "all 0.2s ease-in-out",
                py: { xs: 1.5, md: 1 },
                "&:hover": {
                  borderColor: "#f57c00",
                  backgroundColor: "rgba(255, 152, 0, 0.1)",
                  transform: { xs: "none", md: "translateY(-1px)" },
                },
              }}
            >
              Configurar Impressora
            </Button>
          )}
        </Box>

        {labelError && (
          <Box sx={{ mt: { xs: 2, md: 2 } }}>
            <Alert
              severity="warning"
              sx={{
                maxWidth: { xs: "100%", md: 600 },
                mx: "auto",
                fontSize: { xs: "0.875rem", md: "1rem" },
                mb: 2,
              }}
            >
              {labelError}
            </Alert>
            {onRetry && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  onClick={() => onRetry(true)}
                  disabled={labelLoading}
                  startIcon={
                    labelLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  sx={{
                    minWidth: { xs: "100%", md: 200 },
                    minHeight: { xs: 48, md: "auto" },
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: { xs: "1rem", md: "0.875rem" },
                    borderRadius: 1.5,
                    borderColor: "rgba(255, 152, 0, 0.6)",
                    color: "#ff9800",
                    transition: "all 0.2s ease-in-out",
                    py: { xs: 1.5, md: 1 },
                    "&:hover": {
                      borderColor: "#f57c00",
                      backgroundColor: "rgba(255, 152, 0, 0.1)",
                    },
                  }}
                >
                  {labelLoading ? "Tentando novamente..." : "Tentar Novamente"}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Collapse>
  );
};

export default PrintControls;
